/**
 * Product mention linker.
 *
 * After sanitiseHtml() renders bot text, this module walks text nodes and
 * wraps product name occurrences with clickable links that navigate to the
 * product URL when available, otherwise dispatch a `launchSingleProduct` action.
 *
 * XSS safety: Uses DOM text-node manipulation only — no innerHTML.
 */

import { isSafeUrl } from '../../common/safe-html.js';

export interface ProductMention {
  sku: string;
  short_name: string;
  url?: string;
}

export interface ProductMentionLinkerOptions {
  container: HTMLElement;
  mentions: ProductMention[];
  onProductClick: (mention: ProductMention) => void;
}

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && /[\p{L}\p{N}_]/u.test(char);
}

/**
 * Walk text nodes in `container` and wrap occurrences of each mention's
 * `short_name` with a clickable `<a>` element.
 *
 * Only the first occurrence of each mention is linked to avoid visual clutter.
 */
export function linkProductMentions(options: ProductMentionLinkerOptions): void {
  const { container, mentions, onProductClick } = options;
  if (mentions.length === 0) return;

  // Build a map of lowercase short_name → mention for case-insensitive matching.
  // AGENTIC_ADAPTER_SEAM: keep the linker tolerant of BE shared-schema mentions.
  // Defensive: agent-mode flows may emit mentions with only sku + reason (no
  // short_name). Skip those — the chat bubble still renders without inline links.
  const mentionMap = new Map<string, ProductMention>();
  for (const m of mentions) {
    if (typeof m?.short_name !== 'string' || m.short_name.length === 0) continue;
    mentionMap.set(m.short_name.toLowerCase(), m);
  }

  if (mentionMap.size === 0) return;

  // Process one mention at a time, re-walking the tree each time
  // (DOM mutations invalidate the walker)
  for (const [lowerName, mention] of mentionMap) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    let found = false;

    while (node && !found) {
      // Never wrap inside an existing anchor (e.g. a prior mention link),
      // otherwise mentions could nest inside gengage-product-mention.
      if (node.parentElement?.closest('a')) {
        node = walker.nextNode();
        continue;
      }

      const text = node.textContent ?? '';
      const lowerText = text.toLowerCase();
      // Search for a word-boundary-respecting match; on a failed boundary
      // check keep scanning the same node instead of skipping it entirely.
      let idx = lowerText.indexOf(lowerName);
      while (idx !== -1) {
        const prevChar = idx > 0 ? text[idx - 1] : undefined;
        const nextChar = text[idx + mention.short_name.length];
        if (!isWordChar(prevChar) && !isWordChar(nextChar)) break;
        idx = lowerText.indexOf(lowerName, idx + 1);
      }
      if (idx === -1) {
        node = walker.nextNode();
        continue;
      }

      const before = text.slice(0, idx);
      const match = text.slice(idx, idx + mention.short_name.length);
      const after = text.slice(idx + mention.short_name.length);

      const parent = node.parentNode;
      if (!parent) {
        node = walker.nextNode();
        continue;
      }

      const safeUrl =
        typeof mention.url === 'string' && mention.url.trim() && isSafeUrl(mention.url.trim())
          ? mention.url.trim()
          : undefined;

      const link = document.createElement('a');
      link.className = 'gengage-product-mention';
      link.textContent = match;
      link.href = safeUrl ?? '#';
      link.addEventListener('click', (e) => {
        e.preventDefault();
        onProductClick(mention);
      });

      if (before) parent.insertBefore(document.createTextNode(before), node);
      parent.insertBefore(link, node);
      if (after) parent.insertBefore(document.createTextNode(after), node);
      parent.removeChild(node);

      found = true;
    }
  }
}
